'use client';

import React from 'react';
import { Socket } from 'socket.io-client';
import { ChatState, PartnerInfo } from './ChatLayout';
import { useWebRTC } from '@/hooks/useWebRTC';
import QueueOverlay from './QueueOverlay';

interface VideoPanelProps {
  chatState: ChatState;
  partnerInfo: PartnerInfo | null;
  isMuted: boolean;
  isCameraOff: boolean;
  socket: Socket | null;
  isInitiator: boolean;
}

export default function VideoPanel({
  chatState,
  partnerInfo,
  isMuted,
  isCameraOff,
  socket,
  isInitiator,
}: VideoPanelProps) {
  const { localVideoRef, remoteVideoRef, remoteActive } = useWebRTC(
    socket!,
    true,
    isInitiator,
    isMuted,
    isCameraOff
  );

  // Mobile ke liye alag video refs — same stream sync karo
  const mobileLocalRef = React.useRef<HTMLVideoElement>(null);
  const mobileRemoteRef = React.useRef<HTMLVideoElement>(null);

  React.useEffect(() => {
    if (localVideoRef.current?.srcObject && mobileLocalRef.current) {
      mobileLocalRef.current.srcObject = localVideoRef.current.srcObject;
    }
  });

  React.useEffect(() => {
    if (remoteVideoRef.current?.srcObject && mobileRemoteRef.current) {
      mobileRemoteRef.current.srcObject = remoteVideoRef.current.srcObject;
    }
  });

  return (
    <>
      {/* ── MOBILE: stranger full, your PiP top-right ── */}
      <div className="md:hidden relative w-full h-full bg-gradient-to-br from-[#1a0933] via-[#0f0f1a] to-[#0d1117] rounded-2xl overflow-hidden">

        {/* Stranger — main */}
        <video
          ref={mobileRemoteRef}
          autoPlay
          playsInline
          className={`w-full h-full object-cover ${remoteActive ? 'block' : 'hidden'}`}
        />

        {!remoteActive && chatState === 'chatting' && (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 opacity-40">
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center border border-white/20">
              <svg className="w-8 h-8 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <span className="text-white/40 text-sm">Connecting video...</span>
          </div>
        )}

        {chatState !== 'chatting' && (
          <div className="absolute inset-0 z-10">
            <QueueOverlay chatState={chatState} geoEnabled={false} partnerInfo={partnerInfo ?? undefined} />
          </div>
        )}

        {chatState === 'chatting' && partnerInfo && (
          <div className="absolute bottom-3 left-3 flex items-center gap-2 bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-xl z-20">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-white text-xs font-medium">{partnerInfo?.name || 'Stranger'}</span>
            {partnerInfo.location && (
              <>
                <span className="text-white/30 text-xs">·</span>
                <span className="text-white/70 text-xs">{partnerInfo.location}</span>
              </>
            )}
          </div>
        )}

        {chatState === 'chatting' && partnerInfo && partnerInfo.interests.length > 0 && (
          <div className="absolute top-3 left-3 flex gap-1 flex-wrap max-w-[160px] z-20">
            {partnerInfo.interests.map((interest) => (
              <span key={`partner-interest-${interest}`} className="bg-black/40 backdrop-blur-sm text-white/80 text-xs px-2 py-0.5 rounded-full border border-white/10">
                {interest}
              </span>
            ))}
          </div>
        )}

        {/* YOUR camera — PiP top-right */}
        <div className="absolute top-3 right-3 z-30 w-24 h-32 rounded-2xl overflow-hidden border-2 border-purple-500/50 shadow-[0_0_16px_rgba(139,92,246,0.3)] bg-slate-900">
          {isCameraOff ? (
            <div className="w-full h-full flex items-center justify-center bg-slate-900">
              <svg className="w-6 h-6 text-white/20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
          ) : (
            <video ref={mobileLocalRef} autoPlay playsInline muted className="w-full h-full object-cover scale-x-[-1]" />
          )}
          <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap">
            You {isMuted && '🔇'}
          </div>
        </div>
      </div>

      {/* ── DESKTOP: side-by-side layout (same as before) ── */}
      <div className="hidden md:flex w-full h-full gap-1.5 p-2 bg-gradient-to-br from-[#1a0933] via-[#0f0f1a] to-[#0d1117]">

        {/* Left — Apna Camera */}
        <div className="relative flex-1 rounded-3xl overflow-hidden bg-slate-900 border border-purple-500/20 shadow-[0_0_20px_rgba(139,92,246,0.15)]">
          {isCameraOff ? (
            <div className="w-full h-full flex items-center justify-center">
              <svg className="w-10 h-10 text-white/20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
          ) : (
            <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-cover scale-x-[-1]" />
          )}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-md text-white text-xs px-3 py-1 rounded-full border border-white/20 font-medium">
            You {isMuted && '🔇'}
          </div>
        </div>

        {/* Right — Stranger Camera */}
        <div className="relative flex-1 rounded-3xl overflow-hidden bg-slate-900 border border-green-500/30 shadow-[0_0_25px_rgba(34,197,94,0.2)]">
          <video ref={remoteVideoRef} autoPlay playsInline className={`w-full h-full object-cover ${remoteActive ? 'block' : 'hidden'}`} />

          {!remoteActive && chatState === 'chatting' && (
            <div className="w-full h-full flex flex-col items-center justify-center gap-3 opacity-40">
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center border border-white/20">
                <svg className="w-8 h-8 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <span className="text-white/40 text-sm">Connecting video...</span>
            </div>
          )}

          {chatState !== 'chatting' && (
            <div className="absolute inset-0 z-10">
              <QueueOverlay chatState={chatState} geoEnabled={false} partnerInfo={partnerInfo ?? undefined} />
            </div>
          )}

          {chatState === 'chatting' && partnerInfo && (
            <div className="absolute top-2 left-2 flex items-center gap-2 bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-xl">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-white text-xs font-medium">{partnerInfo?.name || 'Stranger'}</span>
              {partnerInfo.location && (
                <>
                  <span className="text-white/30 text-xs">·</span>
                  <span className="text-white/70 text-xs">{partnerInfo.location}</span>
                </>
              )}
            </div>
          )}

          {chatState === 'chatting' && partnerInfo && partnerInfo.interests.length > 0 && (
            <div className="absolute top-2 right-2 flex gap-1 flex-wrap justify-end max-w-[160px]">
              {partnerInfo.interests.map((interest) => (
                <span key={`partner-interest-${interest}`} className="bg-black/40 backdrop-blur-sm text-white/80 text-xs px-2 py-0.5 rounded-full border border-white/10">
                  {interest}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
